import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { offersAPI } from '../api/client'

export interface OfferChatMessage {
  id: number
  offer_id: number
  sender_id: number
  body: string
  created_at: string
  is_mine: boolean
  sender_role: 'buyer' | 'seller' | string
}

interface OfferChatPanelProps {
  offerId: number
  token: string
  role: 'seller' | 'buyer'
  /** Initial note from offer creation (shown when thread is empty). */
  seedNote?: string | null
  disabled?: boolean
}

export default function OfferChatPanel({
  offerId,
  token,
  role,
  seedNote,
  disabled = false,
}: OfferChatPanelProps) {
  const { t } = useTranslation()
  const [messages, setMessages] = useState<OfferChatMessage[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const scrollRef = useRef<HTMLDivElement | null>(null)

  const loadMessages = useCallback(async () => {
    try {
      const res = await offersAPI.listOfferMessages(offerId, token)
      setMessages(Array.isArray(res.data) ? (res.data as OfferChatMessage[]) : [])
      setError('')
    } catch {
      setError(t('offerChat.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [offerId, token, t])

  useEffect(() => {
    setLoading(true)
    void loadMessages()
    const timer = window.setInterval(() => {
      void loadMessages()
    }, 12000)
    return () => window.clearInterval(timer)
  }, [loadMessages])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [messages, loading])

  async function send(e: React.FormEvent) {
    e.preventDefault()
    const text = input.trim()
    if (!text || sending || disabled) return
    setSending(true)
    setError('')
    try {
      const res = await offersAPI.sendOfferMessage(offerId, text, token)
      const msg = res.data as OfferChatMessage
      setMessages((prev) => [...prev, msg])
      setInput('')
    } catch (err: unknown) {
      const detail =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : null
      setError(detail || t('offerChat.sendFailed'))
    } finally {
      setSending(false)
    }
  }

  const showSeed = !!seedNote?.trim() && messages.length === 0 && !loading

  return (
    <section className="offer-chat" aria-label={t('offerChat.title')}>
      <div className="offer-chat__head">
        <h4 className="offer-chat__title">{t('offerChat.title')}</h4>
        <p className="offer-chat__hint">{t('offerChat.hint')}</p>
      </div>

      <div ref={scrollRef} className="offer-chat__thread">
        {loading && messages.length === 0 && (
          <p className="meta-text offer-chat__empty">{t('common.loading')}</p>
        )}

        {showSeed && (
          <div className="offer-chat-msg offer-chat-msg--seed">
            <div className="offer-chat-msg__bubble">
              <span className="offer-chat-msg__role">
                {role === 'seller' ? t('offerChat.buyer') : t('offerChat.you')}
              </span>
              <p>{seedNote!.trim()}</p>
              <span className="offer-chat-msg__meta">{t('offerChat.initialNote')}</span>
            </div>
          </div>
        )}

        {!loading && messages.length === 0 && !showSeed && (
          <p className="meta-text offer-chat__empty">{t('offerChat.empty')}</p>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`offer-chat-msg${msg.is_mine ? ' offer-chat-msg--mine' : ' offer-chat-msg--theirs'}`}
          >
            <div className="offer-chat-msg__bubble">
              <span className="offer-chat-msg__role">
                {msg.is_mine
                  ? t('offerChat.you')
                  : msg.sender_role === 'seller'
                    ? t('offerChat.seller')
                    : t('offerChat.buyer')}
              </span>
              <p>{msg.body}</p>
              <time className="offer-chat-msg__meta" dateTime={msg.created_at}>
                {new Date(msg.created_at).toLocaleString()}
              </time>
            </div>
          </div>
        ))}
      </div>

      {error && <p className="error-text offer-chat__error">{error}</p>}

      <form className="offer-chat__composer" onSubmit={(e) => void send(e)}>
        <textarea
          rows={2}
          value={input}
          disabled={disabled || sending}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t('offerChat.placeholder')}
          aria-label={t('offerChat.placeholder')}
          maxLength={2000}
        />
        <button type="submit" className="offer-chat__send" disabled={disabled || sending || !input.trim()}>
          {sending ? t('offerChat.sending') : t('offerChat.send')}
        </button>
      </form>
    </section>
  )
}
